/**
 * 상자 드롭 테이블 (drops.tsv) — 골드 상자 등급 확률 + 다이아 상자 강화석 드롭.
 * 수치는 TSV에서 수정 후 `npm run export:data`. (일일상점 rarityRef 테이블은 dailyShopData.ts 소유.)
 */
import dropsJson from "../generated/drops.json";
import { RARITY_ORDER, type ModuleRarity } from "../../modules/ProjectileModule.js";

/**
 * 골드 상자 등급 확률 (drops.tsv `table=goldBoxRarity`). 합 1.0. 추첨은 등급을 먼저 뽑고 해당 등급 정의에서 균등 선택.
 * transcendent=0 → 상자로 나오지 않는 "나중" 컨텐츠 (도감에는 잠금으로 노출).
 */
export const GOLD_BOX_RARITY_WEIGHTS: Record<string, number> = Object.fromEntries(
  dropsJson.filter((d) => d.table === "goldBoxRarity").map((d) => [d.id, d.weight]),
);

/** 다이아 상자 강화석 드롭 테이블 (drops.tsv `table=diamondStone`). weight 비례 누적 추첨. */
export const DIAMOND_BOX_STONE_DROPS: readonly { amount: number; weight: number }[] = dropsJson
  .filter((d) => d.table === "diamondStone")
  .map((d) => ({ amount: d.amount, weight: d.weight }));

/** 등급별 획득 확률 1행 — 확률(?) 팝업 표시용. */
export interface RarityOdds {
  readonly rarity: ModuleRarity;
  /** 합 대비 백분율 (0~100). 소수 1자리 표기는 뷰 책임. */
  readonly percent: number;
}

/**
 * 등급 가중치 → 표시용 확률 목록 (순수) — weight>0 등급만 RARITY_ORDER 순으로, 합 대비 % 산출.
 * 확률(?) 팝업이 GOLD_BOX_RARITY_WEIGHTS를 넣어 등급별 드롭율을 보여주는 데 사용.
 */
export function computeRarityOdds(weights: Record<string, number>): RarityOdds[] {
  const entries = RARITY_ORDER.filter((r) => (weights[r] ?? 0) > 0).map((r) => ({
    rarity: r,
    weight: weights[r]!,
  }));
  const total = entries.reduce((s, e) => s + e.weight, 0);
  if (total <= 0) return [];
  return entries.map((e) => ({ rarity: e.rarity, percent: (e.weight / total) * 100 }));
}
